<template>
  <component :is="wrapperIs" v-bind="wrapperBind">
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      :label-width="formLabelWidth"
      size="medium"
      :class="['tpl-form', { 'form-page-form': !embedded }]"
    >
      <p v-if="!isEdit && !embedded" class="tpl-form__tip">{{ $t('contractTemplateFormPage.newTip') }}</p>

      <el-row v-if="embedded" :gutter="16">
        <el-col :span="12">
          <el-form-item :label="$t('contractTemplateFormPage.name')" prop="templateName">
            <el-input v-model="form.templateName" :placeholder="$t('contractTemplateFormPage.namePh')" clearable />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item :label="$t('contractTemplateFormPage.version')" prop="versionTag">
            <el-input v-model="form.versionTag" :placeholder="$t('contractTemplateFormPage.versionPh')" clearable />
          </el-form-item>
        </el-col>
      </el-row>
      <template v-else>
        <el-form-item :label="$t('contractTemplateFormPage.name')" prop="templateName">
          <el-input v-model="form.templateName" :placeholder="$t('contractTemplateFormPage.namePh')" clearable />
        </el-form-item>
        <el-form-item :label="$t('contractTemplateFormPage.version')" prop="versionTag">
          <el-input v-model="form.versionTag" :placeholder="$t('contractTemplateFormPage.versionPh')" clearable />
        </el-form-item>
      </template>

      <el-row :gutter="16">
        <el-col :span="12">
          <el-form-item :label="$t('contractTemplateFormPage.pageSizeLabel')">
            <el-select v-model="form.pageSize" size="small">
              <el-option :label="$t('contractTemplateFormPage.pageSizeLetter')" value="letter" />
              <el-option :label="$t('contractTemplateFormPage.pageSizeA4')" value="a4" />
              <el-option :label="$t('contractTemplateFormPage.pageSizeLegal')" value="legal" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item :label="$t('contractTemplateFormPage.pageMarginsLabel')">
            <el-input
              v-model="form.pageMarginMm"
              size="small"
              :placeholder="$t('contractTemplateFormPage.pageMarginsPlaceholder')"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item :label="$t('contractTemplateFormPage.insertPlaceholderLabel')">
        <el-select
          v-model="placeholderToInsert"
          size="small"
          filterable
          :placeholder="$t('contractTemplateFormPage.insertPlaceholderPh')"
          :loading="phLoading"
          style="max-width: 380px"
          @change="onInsertPlaceholder"
        >
          <el-option
            v-for="row in placeholderRows"
            :key="row.key"
            :label="row.example + '  -  ' + row.description"
            :value="row.example"
          />
        </el-select>
        <el-button size="small" type="text" style="margin-left: 8px" @click="placeholderDialogVisible = true">
          {{ $t('contractTemplateFormPage.viewAllPlaceholders') }}
        </el-button>
      </el-form-item>

      <el-form-item :label="$t('contractTemplateFormPage.htmlBodyLabel')" prop="htmlContent">
        <el-input
          ref="htmlEditor"
          v-model="form.htmlContent"
          type="textarea"
          :autosize="{ minRows: 14, maxRows: 28 }"
          :placeholder="htmlBodyPlaceholderHint"
          class="tpl-form__html-editor"
        />
        <p class="tpl-form__html-hint tpl-form__html-hint--inline-tip">
          <span>{{ $t('contractTemplateFormPage.htmlBodyTipPrefix') }}</span>
          <code v-pre>{{customer_first_name}}</code>
          <span>{{ $t('contractTemplateFormPage.htmlBodyTipSuffix') }}</span>
        </p>
      </el-form-item>

      <el-form-item :label="$t('contractTemplateFormPage.livePreviewLabel')">
        <div class="tpl-form__preview-wrap">
          <iframe ref="previewFrame" class="tpl-form__preview-frame" sandbox=""></iframe>
          <p class="tpl-form__html-hint tpl-form__html-hint--inline-tip">
            {{ $t('contractTemplateFormPage.livePreviewHint') }}
          </p>
        </div>
      </el-form-item>

      <el-alert
        v-if="legacyDocxBanner"
        type="warning"
        :closable="false"
        show-icon
        :title="$t('contractTemplateFormPage.legacyDocxHint')"
        style="margin-bottom: 14px"
      />

      <el-form-item v-if="!embedded" class="form-page-form__actions">
        <el-button type="primary" :loading="saving" @click="submit">{{ $t('contractTemplateFormPage.save') }}</el-button>
        <el-button @click="$router.push('/system/contract-templates')">{{ $t('contractTemplateFormPage.backList') }}</el-button>
      </el-form-item>
    </el-form>

    <form-dialog :title="$t('contractTemplateFormPage.placeholderDialogTitle')" :visible.sync="placeholderDialogVisible" width="720px">
      <el-table :data="placeholderRows" border size="small" max-height="420">
        <el-table-column prop="example" :label="$t('contractTemplateFormPage.phColPlaceholder')" min-width="220">
          <template slot-scope="s">
            <code class="tpl-form__ph-code">{{ s.row.example }}</code>
          </template>
        </el-table-column>
        <el-table-column prop="description" :label="$t('contractTemplateFormPage.phColDescription')" min-width="280" />
        <el-table-column :label="$t('contractTemplateFormPage.phColActions')" width="100" align="center">
          <template slot-scope="s">
            <el-button type="text" size="small" @click="copyPlaceholder(s.row.example)">{{ $t('contractTemplateFormPage.copyPlaceholder') }}</el-button>
          </template>
        </el-table-column>
      </el-table>
    </form-dialog>
  </component>
</template>

<script>
import { getMergePlaceholders, getTemplate, saveTemplate, updateTemplate } from '@/api/contractTemplate'
import FormDialog from '@/components/FormDialog.vue'
import FormPageShell from '@/components/FormPageShell.vue'
import { buildDraftSignatureBlockHtml } from '@/utils/contractSignatureBlockHtml'
import {
  applyPaymentPlanPreviewPlaceholders,
  buildPaymentPlanTablePlaceholderPreview,
  buildPaymentPlanTextPlaceholderPreview
} from '@/utils/contractPaymentPlanPreviewHtml'

const PREVIEW_DEBOUNCE_MS = 400

export default {
  name: 'ContractTemplateForm',
  components: { FormDialog, FormPageShell },
  props: {
    embedded: { type: Boolean, default: false },
    recordId: { type: [String, Number], default: null }
  },
  data() {
    return {
      isEdit: false,
      saving: false,
      legacyDocxBanner: false,
      placeholderRows: [],
      placeholderToInsert: '',
      placeholderDialogVisible: false,
      phLoading: false,
      previewTimer: null,
      form: {
        templateName: '',
        versionTag: '',
        templateStatusCode: 1,
        templateKind: 2,
        templateFileUrl: '',
        htmlContent: this.defaultHtmlSkeleton(),
        pageSize: 'letter',
        pageMarginMm: '20 20 20 20'
      }
    }
  },
  computed: {
    /** 弹窗双列时下 label 不能太窄，否则会英文拆词（如 Page margins / Insert placeholder） */
    formLabelWidth() {
      void this.$locale
      return this.embedded ? '178px' : '148px'
    },
    htmlBodyPlaceholderHint() {
      void this.$locale
      return this.$t('contractTemplateFormPage.htmlBodyPh')
    },
    wrapperIs() {
      return this.embedded ? 'div' : 'FormPageShell'
    },
    wrapperBind() {
      void this.$locale
      if (this.embedded) {
        return { class: 'tpl-form__embed' }
      }
      return {
        isEdit: this.isEdit,
        title: this.isEdit ? this.$t('contractTemplateFormPage.editTitle') : this.$t('contractTemplateFormPage.newTitle'),
        subtitle: this.isEdit ? '' : this.$t('contractTemplateFormPage.newSubtitle'),
        icon: 'el-icon-files',
        theme: 'violet'
      }
    },
    rules() {
      void this.$locale
      return {
        templateName: [{ required: true, message: this.$t('contractTemplateFormPage.ruleRequired'), trigger: 'blur' }],
        htmlContent: [
          { required: true, message: this.$t('contractTemplateFormPage.htmlBodyRequired'), trigger: 'blur' }
        ]
      }
    }
  },
  watch: {
    'form.htmlContent'() {
      this.scheduleRefreshPreview()
    }
  },
  async created() {
    this.loadPlaceholders()
    if (this.embedded) {
      const rid = this.recordId
      this.isEdit = rid != null && rid !== ''
      if (this.isEdit) {
        try {
          await this.loadDetail(rid)
        } catch (e) {
          this.$emit('cancel')
        }
      }
      return
    }
    const id = this.$route.params.id
    if (id) {
      this.isEdit = true
      await this.loadDetail(id)
    }
  },
  mounted() {
    this.$nextTick(() => this.refreshPreview())
  },
  beforeDestroy() {
    if (this.previewTimer) {
      clearTimeout(this.previewTimer)
      this.previewTimer = null
    }
  },
  methods: {
    defaultHtmlSkeleton() {
      return this.$t('contractTemplateFormPage.sampleHtmlBody')
    },
    editId() {
      if (this.embedded) return this.recordId
      return this.$route.params.id
    },
    async loadDetail(id) {
      try {
        const r = await getTemplate(id)
        const row = (r && r.data) || r
        if (!row || !row.id) {
          this.$message.error(this.$t('contractTemplateFormPage.notFound'))
          if (!this.embedded) this.$router.replace('/system/contract-templates')
          throw new Error('not found')
        }
        const wasDocx =
          row.templateKind != null && Number(row.templateKind) === 1 && String(row.templateFileUrl || '').trim() !== ''
        this.legacyDocxBanner = wasDocx && (!row.htmlContent || !String(row.htmlContent).trim())
        const html =
          row.htmlContent && String(row.htmlContent).trim()
            ? row.htmlContent
            : this.defaultHtmlSkeleton()
        this.form = {
          templateName: row.templateName || '',
          versionTag: row.versionTag || '',
          templateStatusCode: row.templateStatusCode != null ? row.templateStatusCode : 1,
          templateKind: 2,
          templateFileUrl: '',
          htmlContent: html,
          pageSize: row.pageSize || 'letter',
          pageMarginMm: row.pageMarginMm || '20 20 20 20'
        }
      } catch (e) {
        if (e.message !== 'not found') {
          this.$message.error((e && e.message) || this.$t('contractTemplateFormPage.loadFailed'))
          if (!this.embedded) this.$router.replace('/system/contract-templates')
        }
        throw e
      }
    },
    async loadPlaceholders() {
      this.phLoading = true
      try {
        const r = await getMergePlaceholders()
        const rows = (r && r.data) || r
        this.placeholderRows = Array.isArray(rows) ? rows : []
      } catch (e) {
        this.placeholderRows = []
      } finally {
        this.phLoading = false
      }
    },
    onInsertPlaceholder(value) {
      if (!value) return
      const editor = this.$refs.htmlEditor
      const textareaEl = editor && editor.$refs && editor.$refs.textarea
      if (textareaEl) {
        const start = textareaEl.selectionStart != null ? textareaEl.selectionStart : (this.form.htmlContent || '').length
        const end = textareaEl.selectionEnd != null ? textareaEl.selectionEnd : start
        const before = this.form.htmlContent ? this.form.htmlContent.substring(0, start) : ''
        const after = this.form.htmlContent ? this.form.htmlContent.substring(end) : ''
        this.form.htmlContent = before + value + after
        const cursor = (before + value).length
        this.$nextTick(() => {
          textareaEl.focus()
          textareaEl.setSelectionRange(cursor, cursor)
        })
      } else {
        this.form.htmlContent = (this.form.htmlContent || '') + value
      }
      this.placeholderToInsert = ''
    },
    copyPlaceholder(text) {
      if (!text) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(() => {})
      } else {
        const ta = document.createElement('textarea')
        ta.value = text
        document.body.appendChild(ta)
        ta.select()
        try {
          document.execCommand('copy')
        } catch (e) {
          // ignore
        }
        document.body.removeChild(ta)
      }
      this.$message.success(this.$t('contractTemplateFormPage.copiedToClipboard'))
    },
    scheduleRefreshPreview() {
      if (this.previewTimer) {
        clearTimeout(this.previewTimer)
      }
      this.previewTimer = setTimeout(() => {
        this.refreshPreview()
      }, PREVIEW_DEBOUNCE_MS)
    },
    /** 预览时替换签字区与付款计划等占位符为可见示例。 */
    renderPreviewPlaceholders(html) {
      if (!html) return ''
      const awaiting = this.$t('contractTemplateFormPage.previewSignatureHint')
      const customerBox = buildDraftSignatureBlockHtml('Customer Signature', awaiting)
      const companyBox = buildDraftSignatureBlockHtml('Company Signature', awaiting)
      const planLabels = {
        colIndex: this.$t('contractTemplateFormPage.previewPlanColIndex'),
        colMilestone: this.$t('contractTemplateFormPage.previewPlanColMilestone'),
        colAmount: this.$t('contractTemplateFormPage.previewPlanColAmount'),
        colDue: this.$t('contractTemplateFormPage.previewPlanColDue'),
        colNotes: this.$t('contractTemplateFormPage.previewPlanColNotes')
      }
      const planTable = buildPaymentPlanTablePlaceholderPreview({ labels: planLabels })
      const planText = buildPaymentPlanTextPlaceholderPreview()
      let out = html
        .split('{{customer_signature}}')
        .join(customerBox)
        .split('{{company_signature}}')
        .join(companyBox)
      out = applyPaymentPlanPreviewPlaceholders(out, planTable, planText)
      return out
    },
    renderPreviewSignatureBoxes(html) {
      return this.renderPreviewPlaceholders(html)
    },
    refreshPreview() {
      const frame = this.$refs.previewFrame
      if (!frame) return
      const margin = (this.form.pageMarginMm || '20 20 20 20')
        .split(/\s+/)
        .filter(Boolean)
        .map(s => (/^\d+(\.\d+)?$/.test(s) ? `${s}mm` : s))
        .join(' ') || '20mm 20mm 20mm 20mm'
      const sizeMap = { letter: '215.9mm 279.4mm', a4: '210mm 297mm', legal: '215.9mm 355.6mm' }
      const pageWidth = (sizeMap[this.form.pageSize] || sizeMap.letter).split(' ')[0]
      const bodyHtml = this.renderPreviewPlaceholders(this.form.htmlContent || '')
      const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/><style>
        html, body { margin: 0; background: #f3f4f6; }
        body { padding: 24px 16px; font-family: 'Helvetica', 'Arial', sans-serif; color: #111827; }
        .sheet { background: #fff; width: ${pageWidth}; max-width: 100%; margin: 0 auto; padding: ${margin};
                 box-shadow: 0 6px 20px rgba(15, 23, 42, 0.12); border-radius: 4px; line-height: 1.55; font-size: 12pt; }
        .sheet h1, .sheet h2, .sheet h3, .sheet h4 { color: #0f172a; margin: 14pt 0 6pt; }
        .sheet p { margin: 6pt 0; }
        .sheet table { width: 100%; border-collapse: collapse; margin: 10pt 0; }
        .sheet th, .sheet td { border: 1px solid #d1d5db; padding: 6pt 8pt; vertical-align: top; }
        .sheet th { background: #f3f4f6; }
      </style></head><body><div class="sheet">${bodyHtml}</div></body></html>`
      try {
        frame.srcdoc = html
      } catch (e) {
        const doc = frame.contentDocument || (frame.contentWindow && frame.contentWindow.document)
        if (doc) {
          doc.open()
          doc.write(html)
          doc.close()
        }
      }
    },
    submit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.saving = true
        this.$emit('saving', true)
        try {
          const payload = this.buildPayload()
          if (this.isEdit) {
            await updateTemplate(this.editId(), payload)
            this.$message.success(this.$t('contractTemplateFormPage.saved'))
          } else {
            await saveTemplate(payload)
            this.$message.success(this.$t('contractTemplateFormPage.created'))
          }
          if (this.embedded) {
            this.$emit('saved')
          } else {
            this.$router.push('/system/contract-templates')
          }
        } catch (e) {
          this.$message.error((e && e.message) || this.$t('contractTemplateFormPage.failed'))
        } finally {
          this.saving = false
          this.$emit('saving', false)
        }
      })
    },
    buildPayload() {
      return {
        templateName: this.form.templateName,
        versionTag: this.form.versionTag,
        templateStatusCode: this.form.templateStatusCode,
        templateKind: 2,
        templateFileUrl: null,
        htmlContent: this.form.htmlContent || '',
        pageSize: this.form.pageSize || 'letter',
        pageMarginMm: this.form.pageMarginMm || '20 20 20 20'
      }
    }
  }
}
</script>

<style scoped>
.form-page-form {
  max-width: 100%;
}

.tpl-form__embed {
  min-width: 0;
}

/* 避免标签区过窄时出现 “Page margins (m” “m)” 这类拆词换行 */
.tpl-form ::v-deep .el-form-item__label {
  white-space: nowrap;
  word-break: keep-all;
  line-height: 1.35;
}

.tpl-form__tip {
  margin: 0 0 10px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.6;
  color: var(--dark-text-sub, #8b95a1);
  background: var(--dark-elevated, #f4f7fb);
  border-radius: 8px;
  border-left: 3px solid #409eff;
}

.tpl-form__html-hint--inline-tip {
  white-space: normal;
}

.form-page-form__actions {
  margin-top: 8px;
  margin-bottom: 0;
}

.tpl-form__html-editor ::v-deep .el-textarea__inner {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
  line-height: 1.65;
}

.tpl-form__html-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--dark-text-mute, #6b7280);
  line-height: 1.5;
}

.tpl-form__html-hint code {
  background: rgba(99, 102, 241, 0.1);
  color: #4f46e5;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 12px;
}

.tpl-form__preview-wrap {
  width: 100%;
}

.tpl-form__preview-frame {
  width: 100%;
  height: 520px;
  border: 1px solid var(--color-border-soft, #e5e7eb);
  border-radius: 8px;
  background: #f3f4f6;
}

.tpl-form__ph-code {
  background: rgba(99, 102, 241, 0.1);
  color: #4f46e5;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 12px;
}
</style>
